import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Notice, Progress } from "../components/Notice";
import { PROVIDER_LABEL, describeAuthError, takeLinking, takeReturn } from "../lib/authProviders";

const WAIT_MS = 12_000;

/**
 * Where Google, Apple and identity linking land after the provider.
 * supabase-js reads the tokens or code from the URL on load; this screen
 * only decides what the user sees: an error (cancelled, denied, already
 * connected), or the page they were heading to once a session exists.
 *
 * The error is derived from the URL synchronously, not stored in state,
 * so a callback that carries an error never navigates away first: a user
 * who is already signed in (linking a provider) sees the error, not
 * Account.
 */
export function AuthCallback() {
  const { session } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const error = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
    return describeAuthError(query) ?? describeAuthError(hash);
  }, [location.search, location.hash]);

  useEffect(() => {
    if (error) {
      takeLinking();
      takeReturn();
    }
  }, [error]);

  useEffect(() => {
    if (error || !session) return;
    const linked = takeLinking();
    const target = takeReturn() ?? "/";
    navigate(linked ? `/account?linked=${linked}` : target, { replace: true });
  }, [session, error, navigate]);

  useEffect(() => {
    if (error) return;
    const id = window.setTimeout(() => {
      // Nothing arrived: forget the attempt so a later sign-in starts clean.
      takeLinking();
      takeReturn();
      setTimedOut(true);
    }, WAIT_MS);
    return () => window.clearTimeout(id);
  }, [error]);

  if (error) {
    return (
      <section className="stack" style={{ maxWidth: 420 }}>
        <h1>Sign in</h1>
        <Notice kind="error" title="Sign-in did not complete">
          {error}
        </Notice>
        <Link className="btn btn-primary" to="/signin">
          Back to sign in
        </Link>
      </section>
    );
  }
  if (session === null && timedOut) {
    return (
      <section className="stack" style={{ maxWidth: 420 }}>
        <h1>Sign in</h1>
        <Notice kind="error" title="Sign-in did not complete">
          The provider sent you back without a session. Try again; if it keeps happening, use email and password.
        </Notice>
        <Link className="btn btn-primary" to="/signin">
          Back to sign in
        </Link>
      </section>
    );
  }
  const linking = (() => {
    try {
      return sessionStorage.getItem("soundlayer:auth:linking");
    } catch {
      return null;
    }
  })();
  return (
    <section className="stack">
      <Progress
        label={
          linking && linking in PROVIDER_LABEL
            ? `Connecting ${PROVIDER_LABEL[linking as keyof typeof PROVIDER_LABEL]} to your SoundLayer ID…`
            : "Finishing sign-in…"
        }
      />
    </section>
  );
}
